import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSurfaceTheme } from '@/components/SurfaceTheme/useSurfaceTheme'

import { AboutPreview } from './AboutPreview'
import { HowItWorks } from './how/HowItWorks'
import { SectionHeader } from './SectionHeader'
import { ClosingCta } from './ClosingCta'
import { DemoDialog } from './demo/DemoDialog'
import { DemoProvider } from './demo/DemoStore'
import { FeatureList } from './FeatureList'
import { LandingFooter } from './LandingFooter'
import { LandingHero } from './LandingHero'
import { LandingNav } from './LandingNav'
import { LandingStickyCta } from './LandingStickyCta'
import { NetworkSection } from './network/NetworkSection'
import { PressRail } from './PressRail'
import { TrustBand } from '@/components/TrustBand/TrustBand'
import styles from './Landing.module.scss'

/**
 * The public root. No store: nothing here reads from the service — every number on the page was
 * measured once and written down, and a landing that waits on `/api/health` to render its headline
 * is a landing that shows a spinner to a first-time visitor.
 */
export const Landing = () => {
  useSurfaceTheme('plg')
  const { t } = useTranslation()
  /* Which button opened the form travels with the request, so the page can be read as a funnel:
     "nav" and "closing" are two different levels of intent. */
  const [demoSource, setDemoSource] = useState<string | null>(null)
  /* The two edges of the phone's pinned ask (LandingStickyCta): it comes up once the hero's buttons
     are gone and goes down when the closing block arrives. Refs, not ids: the landing is one screen
     and they never leave it. */
  const heroCtaRef = useRef<HTMLDivElement>(null)
  const closingRef = useRef<HTMLElement>(null)

  return (
    <div className={styles.wrap}>
      <LandingNav onBookDemo={() => setDemoSource('nav')} />

      {/* The first screen claims the viewport minus the nav and centres its two children, so the
          next section always begins below the fold and the hero breathes on tall screens. */}
      <div className={styles.first}>
        <LandingHero ctaRef={heroCtaRef} />
        <TrustBand />
      </div>

      <NetworkSection />
      <FeatureList />
      <PressRail />
      {/* Заменил «Every score shows its parts»: тот блок объяснял устройство оценки, этот —
          путь через продукт, на который у посетителя и есть вопрос. Четыре оси никуда не делись,
          они внутри шага 03. */}
      <section id="how" className={styles.how}>
        <SectionHeader
          kicker={t('landing.how.kicker')}
          title={t('landing.how.title')}
          lede={t('landing.how.lede')}
        />
        <HowItWorks />
      </section>
      <AboutPreview />
      <ClosingCta rootRef={closingRef} onBookDemo={() => setDemoSource('closing')} />
      <LandingFooter />
      <LandingStickyCta heroRef={heroCtaRef} closingRef={closingRef} />

      {demoSource ? (
        <DemoProvider source={demoSource}>
          <DemoDialog onClose={() => setDemoSource(null)} />
        </DemoProvider>
      ) : null}
    </div>
  )
}
